// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { getTable, getTableItem, patchTable, postTable } from '../api/api';
import { PLAN_RANK, PLAN_LABELS } from '../utils/helpers';

// Création du contexte
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Provider du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger l'utilisateur depuis localStorage au démarrage
  useEffect(() => {
    const storedUser = localStorage.getItem('aeii_user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
      } catch (e) {
        console.error('Erreur lors du chargement de la session:', e);
        localStorage.removeItem('aeii_user');
      }
    }
    setLoading(false);
  }, []);

  // Connexion de l'utilisateur
  const login = async (email, password) => {
    setError(null);
    try {
      const res = await getTable('tables/users', { search: email, limit: 200 });
      const users = (res.data || []).filter(
        (u) => u.email?.toLowerCase() === email.toLowerCase()
      );
      
      const found = users[0];
      if (!found) {
        throw new Error('Adresse email ou mot de passe incorrect.');
      }
      
      if (found.password !== password) {
        throw new Error('Adresse email ou mot de passe incorrect.');
      }

      // Créer l'objet session
      const sessionUser = {
        id: found.id,
        full_name: found.full_name,
        email: found.email,
        plan: found.plan || 'gratuit',
        organization: found.organization || '',
        country: found.country || '',
        role: found.role || 'Autre',
        plan_started_at: found.plan_started_at,
        plan_expires_at: found.plan_expires_at,
        newsletter: found.newsletter || 0,
      };

      // Sauvegarder dans localStorage
      localStorage.setItem('aeii_user', JSON.stringify(sessionUser));
      setUser(sessionUser);
      
      return sessionUser;
    } catch (error) {
      setError(error.message);
      throw new Error(error.message || 'Erreur de connexion');
    }
  };

  // Inscription d'un nouvel utilisateur
  const register = async (payload) => {
    setError(null);
    try {
      // Vérifier si l'email existe déjà
      const res = await getTable('tables/users', { search: payload.email, limit: 200 });
      const existing = (res.data || []).filter(
        (u) => u.email?.toLowerCase() === payload.email.toLowerCase()
      );
      
      if (existing.length > 0) {
        throw new Error('Un compte existe déjà avec cette adresse email.');
      }

      // Créer le nouvel utilisateur
      const body = {
        full_name: payload.full_name,
        email: payload.email,
        password: payload.password,
        organization: payload.organization || '',
        country: payload.country || '',
        role: payload.role || 'Autre',
        plan: 'gratuit',
        plan_started_at: new Date().toISOString(),
        plan_expires_at: null,
        newsletter: payload.newsletter ? 1 : 0,
      };

      const created = await postTable('tables/users', body);
      
      // Créer l'objet session
      const sessionUser = {
        id: created.id,
        full_name: created.full_name,
        email: created.email,
        plan: created.plan || 'gratuit',
        organization: created.organization || '',
        country: created.country || '',
        role: created.role || 'Autre',
        plan_started_at: created.plan_started_at,
        plan_expires_at: created.plan_expires_at,
        newsletter: created.newsletter || 0,
      };

      // Sauvegarder dans localStorage
      localStorage.setItem('aeii_user', JSON.stringify(sessionUser));
      setUser(sessionUser);
      
      return sessionUser;
    } catch (error) {
      setError(error.message);
      throw new Error(error.message || 'Erreur lors de l\'inscription');
    }
  };

  // Déconnexion
  const logout = () => {
    localStorage.removeItem('aeii_user');
    setUser(null);
    setError(null);
  };

  // Mise à jour du profil
  const updateProfile = async (data) => {
    setError(null);
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    try {
      const updated = await patchTable('tables/users', user.id, data);
      
      const sessionUser = {
        ...user,
        full_name: updated.full_name || user.full_name,
        organization: updated.organization || user.organization,
        country: updated.country || user.country,
        role: updated.role || user.role,
      };
      
      localStorage.setItem('aeii_user', JSON.stringify(sessionUser));
      setUser(sessionUser);
      
      return sessionUser;
    } catch (error) {
      setError(error.message);
      throw new Error('Erreur lors de la mise à jour du profil');
    }
  };

  // Mise à jour du plan d'abonnement (gratuit - sans paiement)
  const updatePlan = async (planKey) => {
    setError(null);
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    // Vérifier que le plan est valide
    if (!['gratuit', 'classique', 'premium'].includes(planKey)) {
      throw new Error('Plan invalide');
    }

    try {
      const now = new Date();
      const expires = new Date(now);
      expires.setFullYear(expires.getFullYear() + 1);
      
      const body = {
        plan: planKey,
        plan_started_at: now.toISOString(),
        plan_expires_at: planKey === 'gratuit' ? null : expires.toISOString(),
      };
      
      const updated = await patchTable('tables/users', user.id, body);
      
      const sessionUser = {
        ...user,
        plan: updated.plan,
        plan_started_at: updated.plan_started_at,
        plan_expires_at: updated.plan_expires_at,
      };
      
      localStorage.setItem('aeii_user', JSON.stringify(sessionUser));
      setUser(sessionUser);
      
      return sessionUser;
    } catch (error) {
      setError(error.message);
      throw new Error('Erreur lors de la mise à jour de l\'abonnement');
    }
  };

  // Créer une transaction de paiement Monetbil
  const createPayment = async (plan) => {
    setError(null);
    if (!user) {
      throw new Error('Veuillez vous connecter pour effectuer un paiement');
    }

    // Vérifier que le plan est valide (payant)
    if (!['classique', 'premium'].includes(plan)) {
      throw new Error('Plan invalide pour le paiement');
    }

    // Vérifier que l'utilisateur n'a pas déjà ce plan
    if (user.plan === plan) {
      throw new Error(`Vous avez déjà l'abonnement ${PLAN_LABELS[plan]}`);
    }

    try {
      const response = await fetch('http://localhost:5000/api/payment/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          plan: plan,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du paiement');
      }

      const data = await response.json();
      
      // Sauvegarder la transaction en session pour le retour
      sessionStorage.setItem('aeii_pending_transaction', JSON.stringify({
        reference: data.transactionId,
        plan: plan,
        amount: data.amount,
        timestamp: Date.now()
      }));

      return data;
    } catch (error) {
      setError(error.message);
      throw new Error(error.message || 'Erreur lors de la création du paiement');
    }
  };

  // Vérifier le statut d'une transaction
  const checkPaymentStatus = async (reference) => {
    try {
      const response = await fetch(`http://localhost:5000/api/payment/status/${reference}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du paiement');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      setError(error.message);
      throw new Error(error.message || 'Erreur lors de la vérification');
    }
  };

  // Annuler une transaction
  const cancelPayment = async (reference) => {
    try {
      const response = await fetch('http://localhost:5000/api/payment/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reference }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'annulation');
      }

      sessionStorage.removeItem('aeii_pending_transaction');
      return await response.json();
    } catch (error) {
      setError(error.message);
      throw new Error(error.message || 'Erreur lors de l\'annulation');
    }
  };

  // Rafraîchir les informations de l'utilisateur depuis le serveur
  const refreshUser = async () => {
    if (!user) return null;

    try {
      const fresh = await getTableItem('tables/users', user.id);
      
      if (!fresh) {
        // L'utilisateur n'existe plus
        localStorage.removeItem('aeii_user');
        setUser(null);
        return null;
      }

      const sessionUser = {
        id: fresh.id,
        full_name: fresh.full_name,
        email: fresh.email,
        plan: fresh.plan || 'gratuit',
        organization: fresh.organization || '',
        country: fresh.country || '',
        role: fresh.role || 'Autre',
        plan_started_at: fresh.plan_started_at,
        plan_expires_at: fresh.plan_expires_at,
        newsletter: fresh.newsletter || 0,
      };

      localStorage.setItem('aeii_user', JSON.stringify(sessionUser));
      setUser(sessionUser);
      
      return sessionUser;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
      return user;
    }
  };

  // Vérifier si l'utilisateur peut accéder à un contenu
  const canAccess = (requiredLevel) => {
    if (!user) return false;
    const u = PLAN_RANK[user.plan] ?? 0;
    const r = PLAN_RANK[requiredLevel] ?? 0;
    return u >= r;
  };

  // Vérifier si l'abonnement est actif
  const isPlanActive = (planKey) => {
    if (!user) return false;
    if (planKey === 'gratuit') return true;
    if (user.plan !== planKey) return false;
    
    // Vérifier la date d'expiration
    if (user.plan_expires_at) {
      const expires = new Date(user.plan_expires_at);
      return expires > new Date();
    }
    return true;
  };

  // Obtenir le libellé du plan
  const getPlanLabel = (planKey) => {
    return PLAN_LABELS[planKey] || planKey;
  };

  // Vérifier si l'utilisateur est authentifié
  const isAuthenticated = !!user;

  // Valeurs exposées par le contexte
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    updatePlan,
    createPayment,
    checkPaymentStatus,
    cancelPayment,
    refreshUser,
    canAccess,
    isPlanActive,
    getPlanLabel,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;